import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

export type BvSubscriptionPlanKey = 'essential' | 'annual';

export interface BvSubscriptionCard {
  id: string;
  /**
   * Identifies which package_list_v2 plan this card sells. Matched against
   * the API's plan name via an exact normalized allowlist (see
   * BV_PLAN_NAME_MATCHES) — never a substring test, so a future
   * "Semi-Annual"/"Essential Plus" tier cannot silently sell the wrong plan.
   */
  planKey: BvSubscriptionPlanKey;
  title: string;
  description: string;
  /** "About" copy shown in the upsell modal. */
  learnMoreAbout: string;
  /** "What's included" bullets shown in the upsell modal. */
  includedItems: Array<{ title: string; description?: string }>;
  imageSrc: string;
  /**
   * Static "starts from" display price (USD) — must be the NATIONWIDE FLOOR,
   * since the card advertises "Starts from". Live prices come from
   * package_list_v2 and vary by ZIP (NY/NJ labs cost more), so the modal
   * shows the exact price once a ZIP resolves.
   * Verified 2026-08-25 against walk-in pricing in Chicago, SF, Austin,
   * Miami, Seattle, Atlanta, Boston and Anchorage: Essential $99,
   * Annual $499. (New York 10001 is the outlier at $149/$799 — do NOT
   * source this number from a NY ZIP.)
   */
  fromPrice: number;
  billingNote: string;
  tag?: string;
}

const BV_SHOP_IMAGE_BASE_PATH = 'web_v2/shop/blood-vision';

const bloodVisionSubscriptionCards: BvSubscriptionCard[] = [
  {
    id: 'bv-subscription-essential',
    planKey: 'essential',
    title: 'Blood Vision Essential Plan',
    description:
      '60+ biomarker blood test with Blood Age and AI-powered clinician reports.',
    learnMoreAbout:
      'A foundational blood test built for most people. 60+ biomarkers tested half-yearly reveal patterns across metabolic, hormonal, and cardiovascular health, with Blood Age and an AI-powered clinical summary based on your results.',
    includedItems: [
      {
        title: '1 foundational test',
        description: 'Essential markers, actionable results.',
      },
      {
        title: '60+ biomarkers tested half-yearly',
        description:
          'Visualize complex health data, understand patterns, and discover your blood age.',
      },
      {
        title: 'Blood Age',
        description:
          'A detailed perspective on your health with an AI-powered summary based on your test results.',
      },
      { title: 'AI Clinical Summary with Supplement Report' },
    ],
    imageSrc: `${BV_SHOP_IMAGE_BASE_PATH}/essential-plan.png`,
    fromPrice: 99,
    billingNote: 'Renews half-yearly',
  },
  {
    id: 'bv-subscription-annual',
    planKey: 'annual',
    title: 'Blood Vision Annual Plan',
    description:
      '100+ biomarker blood and urine test, plus a 60+ biomarker follow-up, advanced Blood Age and AI-powered clinician reports.',
    learnMoreAbout:
      'The gold standard. Start with a 100+ biomarker blood and urine panel, then retest 60+ markers at 3 to 6 months to see how your interventions are working, with advanced Blood Age and an AI-powered clinical summary.',
    includedItems: [
      {
        title: '2 comprehensive tests',
        description:
          'Start with 100+ blood and urine biomarkers, followed by a 60+ biomarker retest in 3-6 months.',
      },
      {
        title: 'Track changes over time',
        description:
          'Visualize complex health data, understand patterns, and discover your blood age with comprehensive aging markers.',
      },
      {
        title: 'Advanced Blood Age',
        description:
          'A detailed perspective on your health with an AI-powered summary based on your test results.',
      },
      { title: 'AI Clinical Summary with Supplement Report' },
    ],
    imageSrc: `${BV_SHOP_IMAGE_BASE_PATH}/annual-plan.png`,
    fromPrice: 499,
    billingNote: 'Renews annually',
  },
];

/**
 * Exact (lowercased, trimmed) package_list_v2 plan names each card may sell.
 * Matching fails closed: if a ZIP's response yields zero or more than one
 * match, the modal reports unavailable rather than guessing a plan — a wrong
 * guess would charge the customer for a different product.
 */
export const BV_PLAN_NAME_MATCHES: Record<BvSubscriptionPlanKey, string[]> = {
  essential: ['essential plan'],
  annual: ['annual plan'],
};

export function getBloodVisionSubscriptionCards(
  store: ShopifyStore | null,
): BvSubscriptionCard[] {
  // US-only: Essential/Annual run on the Stripe/Flex subscription rail
  // (POST /api/v1/subscriptions), which the backend supports for the US
  // alone. Adding card data above must not expose the cards elsewhere —
  // widen this gate deliberately if the rail ever launches in new regions.
  if (store !== ShopifyStore.US) return [];

  return bloodVisionSubscriptionCards.filter(
    // Self-hide guard: misconfigured cards render nothing.
    (card) => card.id.length > 0 && card.fromPrice > 0,
  );
}
